import React from "react";
import { observer } from "mobx-react";
import PropTypes from "prop-types";
import { Table } from "semantic-ui-react";
import ProductId from "~/constants/ProductId";
import CreditCostText from "~/applications/spa/component/ui/CreditCostText";

const PricingMethodPerSquareFeetBracketsTable = observer(
  ({ newEstimatedShootSize, productDiscountedCreditCosts, hasBasicFloorPlan, hasEnhancedFloorPlan }) => {
    return (
      <>
        <p>The price for an Immersive 3D Tour is:</p>
        <Table celled textAlign="center">
          <Table.Body>
            <Table.Row active>
              <Table.Cell colSpan="3">
                <strong>Single Property Website</strong>
                <br />
                <CreditCostText cost={productDiscountedCreditCosts[ProductId.PUBLISH_TOUR]} />
              </Table.Cell>
            </Table.Row>
            <Table.Row active>
              <Table.Cell>
                <strong>
                  Estimated
                  <br /> square footage
                </strong>
              </Table.Cell>
              <Table.Cell>
                <strong>
                  Immersive 3D Tour with
                  <br /> a basic floor plan
                </strong>
              </Table.Cell>
              <Table.Cell>
                <strong>
                  Immersive 3D Tour with
                  <br /> an enhanced floor plan
                </strong>
              </Table.Cell>
            </Table.Row>
            {(!newEstimatedShootSize || newEstimatedShootSize <= 2500) && (
              <Table.Row>
                <Table.Cell>Up to 2,500</Table.Cell>
                <Table.Cell active={hasBasicFloorPlan}>
                  {productDiscountedCreditCosts[ProductId.IMMERSIVE_3D_TOUR_0_2500_SQ_FT]} credits
                </Table.Cell>
                <Table.Cell active={hasEnhancedFloorPlan}>
                  {productDiscountedCreditCosts[ProductId.IMMERSIVE_3D_TOUR_WITH_FLOOR_PLAN_0_2500_SQ_FT]} credits
                </Table.Cell>
              </Table.Row>
            )}
            {(!newEstimatedShootSize || (newEstimatedShootSize >= 2501 && newEstimatedShootSize <= 3500)) && (
              <Table.Row>
                <Table.Cell>2,501 - 3,500</Table.Cell>
                <Table.Cell active={hasBasicFloorPlan}>
                  {productDiscountedCreditCosts[ProductId.IMMERSIVE_3D_TOUR_2501_3500_SQ_FT]} credits
                </Table.Cell>
                <Table.Cell active={hasEnhancedFloorPlan}>
                  {productDiscountedCreditCosts[ProductId.IMMERSIVE_3D_TOUR_WITH_FLOOR_PLAN_2501_3500_SQ_FT]} credits
                </Table.Cell>
              </Table.Row>
            )}
            {(!newEstimatedShootSize || (newEstimatedShootSize >= 3501 && newEstimatedShootSize <= 5000)) && (
              <Table.Row>
                <Table.Cell>3,501 - 5,000</Table.Cell>
                <Table.Cell active={hasBasicFloorPlan}>
                  {productDiscountedCreditCosts[ProductId.IMMERSIVE_3D_TOUR_3501_5000_SQ_FT]} credits
                </Table.Cell>
                <Table.Cell active={hasEnhancedFloorPlan}>
                  {productDiscountedCreditCosts[ProductId.IMMERSIVE_3D_TOUR_WITH_FLOOR_PLAN_3501_5000_SQ_FT]} credits
                </Table.Cell>
              </Table.Row>
            )}
            {(!newEstimatedShootSize || (newEstimatedShootSize >= 5001 && newEstimatedShootSize <= 7500)) && (
              <Table.Row>
                <Table.Cell>5,001 - 7,500</Table.Cell>
                <Table.Cell active={hasBasicFloorPlan}>
                  {productDiscountedCreditCosts[ProductId.IMMERSIVE_3D_TOUR_5001_7500_SQ_FT]} credits
                </Table.Cell>
                <Table.Cell active={hasEnhancedFloorPlan}>
                  {productDiscountedCreditCosts[ProductId.IMMERSIVE_3D_TOUR_WITH_FLOOR_PLAN_5001_7500_SQ_FT]} credits
                </Table.Cell>
              </Table.Row>
            )}
            {(!newEstimatedShootSize || (newEstimatedShootSize >= 7501 && newEstimatedShootSize <= 10000)) && (
              <Table.Row>
                <Table.Cell>7,501 - 10,000</Table.Cell>
                <Table.Cell active={hasBasicFloorPlan}>
                  {productDiscountedCreditCosts[ProductId.IMMERSIVE_3D_TOUR_7501_10000_SQ_FT]} credits
                </Table.Cell>
                <Table.Cell active={hasEnhancedFloorPlan}>
                  {productDiscountedCreditCosts[ProductId.IMMERSIVE_3D_TOUR_WITH_FLOOR_PLAN_7501_10000_SQ_FT]} credits
                </Table.Cell>
              </Table.Row>
            )}
            {(!newEstimatedShootSize || newEstimatedShootSize > 10000) && (
              <Table.Row>
                <Table.Cell>More than 10,000</Table.Cell>
                <Table.Cell active={hasBasicFloorPlan}>
                  {productDiscountedCreditCosts[ProductId.IMMERSIVE_3D_TOUR_1000_SQ_FT]} credits
                  <br /> per 1000 sq. ft.
                </Table.Cell>
                <Table.Cell active={hasEnhancedFloorPlan}>
                  {productDiscountedCreditCosts[ProductId.IMMERSIVE_3D_TOUR_WITH_FLOOR_PLAN_1000_SQ_FT]} credits
                  <br /> per 1000 sq. ft.
                </Table.Cell>
              </Table.Row>
            )}
          </Table.Body>
        </Table>
      </>
    );
  },
);

PricingMethodPerSquareFeetBracketsTable.propTypes = {
  newEstimatedShootSize: PropTypes.number,
  hasBasicFloorPlan: PropTypes.bool,
  hasEnhancedFloorPlan: PropTypes.bool,
  productDiscountedCreditCosts: PropTypes.shape({
    [ProductId.PUBLISH_TOUR]: PropTypes.number.isRequired,
    [ProductId.IMMERSIVE_3D_TOUR_0_2500_SQ_FT]: PropTypes.number.isRequired,
    [ProductId.IMMERSIVE_3D_TOUR_WITH_FLOOR_PLAN_0_2500_SQ_FT]: PropTypes.number.isRequired,
    [ProductId.IMMERSIVE_3D_TOUR_2501_3500_SQ_FT]: PropTypes.number.isRequired,
    [ProductId.IMMERSIVE_3D_TOUR_WITH_FLOOR_PLAN_2501_3500_SQ_FT]: PropTypes.number.isRequired,
    [ProductId.IMMERSIVE_3D_TOUR_3501_5000_SQ_FT]: PropTypes.number.isRequired,
    [ProductId.IMMERSIVE_3D_TOUR_WITH_FLOOR_PLAN_3501_5000_SQ_FT]: PropTypes.number.isRequired,
    [ProductId.IMMERSIVE_3D_TOUR_5001_7500_SQ_FT]: PropTypes.number.isRequired,
    [ProductId.IMMERSIVE_3D_TOUR_WITH_FLOOR_PLAN_5001_7500_SQ_FT]: PropTypes.number.isRequired,
    [ProductId.IMMERSIVE_3D_TOUR_7501_10000_SQ_FT]: PropTypes.number.isRequired,
    [ProductId.IMMERSIVE_3D_TOUR_WITH_FLOOR_PLAN_7501_10000_SQ_FT]: PropTypes.number.isRequired,
  }).isRequired,
};

PricingMethodPerSquareFeetBracketsTable.defaultProps = {
  hasBasicFloorPlan: false,
  hasEnhancedFloorPlan: false,
};

export default PricingMethodPerSquareFeetBracketsTable;
